import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  makeStyles, tokens,
  Field, Input, Textarea, Button, Spinner,
} from '@fluentui/react-components';
import { API_BASE, CONTACT_PHONE, CONTACT_PHONE_HREF, CONTACT_EMAIL, CONTACT_EMAIL_HREF } from '../config.js';
import { Heading, Container, PageSection, Card } from '../theme/primitives';
import { iconMap } from '../theme/icons';
import { stonePalette } from '../theme/brand';
import cooperateImg from '../assets/img/marketing/cooperate.jpg';

const CheckIcon = iconMap.check;
const ErrorIcon = iconMap.error;
const SendIcon = iconMap.send;
const PhoneIcon = iconMap.phone;
const MailIcon = iconMap.mail;
const HandshakeIcon = iconMap.handshake;

const BENEFITS = [
  { icon: 'storefront', titleKey: 'cooperate.benefit_0_title', textKey: 'cooperate.benefit_0_text' },
  { icon: 'design_services', titleKey: 'cooperate.benefit_1_title', textKey: 'cooperate.benefit_1_text' },
  { icon: 'local_shipping', titleKey: 'cooperate.benefit_2_title', textKey: 'cooperate.benefit_2_text' },
  { icon: 'handshake', titleKey: 'cooperate.benefit_3_title', textKey: 'cooperate.benefit_3_text' },
];

function FormField({ label, rows, ...props }) {
  return (
    <Field label={label}>
      {rows
        ? <Textarea resize="none" rows={rows} {...props} />
        : <Input {...props} />}
    </Field>
  );
}

const useStyles = makeStyles({
  root: { backgroundColor: stonePalette[50] },

  // Balta (ne paveldėta stonePalette[50]) — žr. #8: sekcija su nuotrauka
  // turi būti balta visuose šios struktūros puslapiuose.
  hero: {
    backgroundColor: tokens.colorNeutralBackground1,
    paddingTop: tokens.spacingVerticalXXXL, paddingBottom: tokens.spacingVerticalXXXL,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  // Paddingas ant heroInner (ne hero) — kai maxWidth-centravimas IR paddingas
  // yra ant SKIRTINGŲ elementų, susidaro dvigubas centravimas, kuris duoda
  // kitokį kairįjį atitraukimą nei header'io. Sudėjus abu ant to paties
  // elemento, formulė identiška TopBar.jsx/MainMenu.jsx `container` (žr.
  // /admin/bugs "Header logotipas ir language icon turi lygiuoti su
  // puslapio turiniu").
  heroInner: {
    maxWidth: '80rem', marginLeft: 'auto', marginRight: 'auto',
    paddingLeft: tokens.spacingHorizontalXXL, paddingRight: tokens.spacingHorizontalXXL,
    display: 'grid', gridTemplateColumns: '1fr', gap: tokens.spacingHorizontalXXL, alignItems: 'center',
    '@media (min-width: 900px)': { gridTemplateColumns: '1fr 1fr' },
    '@media (min-width: 1024px)': { paddingLeft: '3rem', paddingRight: '3rem' },
  },
  heroEyebrow: { marginBottom: tokens.spacingVerticalS },
  title: { marginBottom: tokens.spacingVerticalM },
  heroText: { fontSize: tokens.fontSizeBase400, color: tokens.colorNeutralForeground2, lineHeight: '1.6', marginBottom: tokens.spacingVerticalL, maxWidth: '32rem' },
  heroPoints: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  heroPoint: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground1 },
  heroPointIcon: { color: tokens.colorBrandForeground2, fontSize: '18px', flexShrink: 0 },
  heroImageWrap: {
    borderRadius: tokens.borderRadiusXLarge, overflow: 'hidden', boxShadow: tokens.shadow16,
    aspectRatio: '3 / 2',
  },
  heroImage: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },

  successRoot: { minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: tokens.colorNeutralBackground2, paddingLeft: tokens.spacingHorizontalXXL, paddingRight: tokens.spacingHorizontalXXL },
  successInner: { textAlign: 'center', maxWidth: '24rem' },
  successIconChip: {
    width: '80px', height: '80px', backgroundColor: tokens.colorBrandBackground, borderRadius: tokens.borderRadiusCircular,
    display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: 'auto', marginRight: 'auto',
    marginBottom: tokens.spacingVerticalXXL, color: tokens.colorNeutralForegroundOnBrand,
    boxShadow: '0 8px 32px rgba(9,170,137,0.4)',
  },
  successTitle: { marginBottom: tokens.spacingVerticalM },
  successText: { marginBottom: tokens.spacingVerticalXXL },

  benefitsSection: { backgroundColor: tokens.colorNeutralBackground2 },
  benefitsHeader: { textAlign: 'center', marginBottom: '3.5rem' },
  eyebrow: { display: 'block', marginBottom: tokens.spacingVerticalS },
  benefitsGrid: {
    display: 'grid', gridTemplateColumns: '1fr', gap: tokens.spacingHorizontalL,
    '@media (min-width: 640px)': { gridTemplateColumns: '1fr 1fr' },
    '@media (min-width: 1024px)': { gridTemplateColumns: '1fr 1fr 1fr 1fr' },
  },
  benefitCard: { textAlign: 'center' },
  benefitIconChip: {
    width: '56px', height: '56px', backgroundColor: tokens.colorBrandBackground2, borderRadius: tokens.borderRadius4XLarge,
    display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: 'auto', marginRight: 'auto',
    marginBottom: tokens.spacingVerticalL, color: tokens.colorBrandForeground2, fontSize: '28px',
    transitionProperty: 'background-color', transitionDuration: tokens.durationNormal,
  },
  benefitTitle: { fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1, marginBottom: tokens.spacingVerticalSNudge },
  benefitText: { fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground2, lineHeight: '1.5' },

  formSection: {},
  formGrid: {
    display: 'grid', gridTemplateColumns: '1fr', gap: '4rem', alignItems: 'flex-start',
    '@media (min-width: 1024px)': { gridTemplateColumns: '2fr 3fr' },
  },
  leftEyebrow: { display: 'block', marginBottom: tokens.spacingVerticalM },
  leftTitle: { marginBottom: tokens.spacingVerticalM },
  leftRule: { width: '48px', height: '2px', backgroundColor: tokens.colorBrandBackground, marginBottom: tokens.spacingVerticalL },
  leftLead: { marginBottom: tokens.spacingVerticalL },
  contactList: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  contactRow: {
    display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, color: tokens.colorNeutralForeground2,
    textDecorationLine: 'none', transitionProperty: 'color', transitionDuration: tokens.durationNormal,
    ':hover': { color: tokens.colorBrandForeground2 },
  },
  contactIcon: { fontSize: '18px', color: tokens.colorBrandForeground2, flexShrink: 0 },
  contactText: { fontSize: tokens.fontSizeBase300, fontWeight: tokens.fontWeightMedium },

  formCard: { boxShadow: tokens.shadow8 },
  formTitle: { fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1, marginBottom: tokens.spacingVerticalXL },
  form: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXL },
  fieldsGrid: {
    display: 'grid', gridTemplateColumns: '1fr', gap: tokens.spacingHorizontalXL,
    '@media (min-width: 640px)': { gridTemplateColumns: '1fr 1fr' },
  },
  errorBox: {
    display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, padding: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorStatusDangerBackground1, border: `1px solid ${tokens.colorStatusDangerBorder1}`,
    borderRadius: tokens.borderRadiusXLarge, fontSize: tokens.fontSizeBase300, color: tokens.colorStatusDangerForeground1,
  },
  submitBtn: { width: '100%', justifyContent: 'center' },
});

function Cooperate() {
  const s = useStyles();
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { document.title = `${t('cooperate.title')} | Fitit`; }, [t]);

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      setError(t('common.required_fields'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/contact-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          message: `[Bendradarbiavimas] Įmonė: ${form.company || '—'}\n\n${form.message}`,
          type: 'cooperation',
        }),
      });
      if (!res.ok) throw new Error(t('cooperate.server_error'));
      setSuccess(true);
      setForm({ name: '', company: '', email: '', phone: '', message: '' });
    } catch {
      setError(t('cooperate.submit_error'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className={s.successRoot}>
        <div className={s.successInner}>
          <div className={s.successIconChip}>
            <CheckIcon fontSize={40} />
          </div>
          <Heading variant="h2" className={s.successTitle}>{t('cooperate.success_title')}</Heading>
          <Heading variant="bodyLarge" as="p" className={s.successText}>
            {t('cooperate.success_text')}
          </Heading>
          <Button appearance="outline" size="large" onClick={() => setSuccess(false)}>
            {t('cooperate.send_another')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={s.root}>

      {/* Hero */}
      <div className={s.hero}>
        <div className={s.heroInner}>
          <div>
            <Heading as="h1" variant="h1" className={s.title}>{t('cooperate.title')}</Heading>
            <p className={s.heroText}>
              {t('cooperate.hero_text')}
            </p>
            <div className={s.heroPoints}>
              <div className={s.heroPoint}><HandshakeIcon className={s.heroPointIcon} />{t('cooperate.hero_point_0')}</div>
              <div className={s.heroPoint}><CheckIcon className={s.heroPointIcon} />{t('cooperate.hero_point_1')}</div>
            </div>
          </div>
          <div className={s.heroImageWrap}>
            <img src={cooperateImg} alt={t('cooperate.img_alt')} className={s.heroImage} />
          </div>
        </div>
      </div>

      {/* Benefits */}
      <PageSection className={s.benefitsSection}>
        <Container size="xl">
          <div className={s.benefitsHeader}>
            <Heading as="span" variant="eyebrow" className={s.eyebrow}>{t('cooperate.benefits_eyebrow')}</Heading>
            <Heading variant="section">{t('cooperate.benefits_title')}</Heading>
          </div>
          <div className={s.benefitsGrid}>
            {BENEFITS.map(({ icon, titleKey, textKey }) => {
              const BenefitIcon = iconMap[icon];
              return (
                <Card key={titleKey} hover padding={tokens.spacingHorizontalXXXL} className={s.benefitCard}>
                  <div className={s.benefitIconChip}>
                    <BenefitIcon />
                  </div>
                  <Heading variant="h3" as="h3" className={s.benefitTitle}>{t(titleKey)}</Heading>
                  <p className={s.benefitText}>{t(textKey)}</p>
                </Card>
              );
            })}
          </div>
        </Container>
      </PageSection>

      {/* Contact form */}
      <PageSection className={s.formSection}>
        <Container size="xl">
          <div className={s.formGrid}>

            {/* Left */}
            <div>
              <Heading as="span" variant="eyebrow" className={s.leftEyebrow}>{t('cooperate.left_eyebrow')}</Heading>
              <Heading variant="h2" className={s.leftTitle}>{t('cooperate.left_title')}</Heading>
              <div className={s.leftRule} />
              <Heading variant="bodyLarge" as="p" className={s.leftLead}>
                {t('cooperate.left_lead_1')}
              </Heading>
              <Heading variant="bodyLarge" as="p" className={s.leftLead}>
                {t('cooperate.left_lead_2')}
              </Heading>
              <div className={s.contactList}>
                {[
                  { icon: PhoneIcon, text: CONTACT_PHONE, href: CONTACT_PHONE_HREF },
                  { icon: MailIcon, text: CONTACT_EMAIL, href: CONTACT_EMAIL_HREF },
                ].map(({ icon: RowIcon, text, href }) => (
                  <a key={text} href={href} className={s.contactRow}>
                    <RowIcon className={s.contactIcon} />
                    <span className={s.contactText}>{text}</span>
                  </a>
                ))}
              </div>
            </div>

            {/* Form */}
            <div>
              <Card className={s.formCard} padding={tokens.spacingHorizontalXXL}>
                <Heading variant="h3" as="h3" className={s.formTitle}>{t('cooperate.form_title')}</Heading>
                <form onSubmit={handleSubmit} className={s.form}>
                  <div className={s.fieldsGrid}>
                    <FormField label={t('cooperate.field_name')} name="name" value={form.name} onChange={handleChange} placeholder="Jonas Jonaitis" required />
                    <FormField label={t('cooperate.field_company')} name="company" value={form.company} onChange={handleChange} placeholder="UAB Pavyzdys" />
                    <FormField label={t('cooperate.field_email')} name="email" type="email" value={form.email} onChange={handleChange} placeholder="jonas@pvz.lt" required />
                    <FormField label={t('cooperate.field_phone')} name="phone" value={form.phone} onChange={handleChange} placeholder="+370 600 00000" required />
                  </div>
                  <FormField label={t('cooperate.field_message')} name="message" value={form.message} onChange={handleChange} placeholder={t('cooperate.field_message_ph')} rows={4} />
                  {error && (
                    <div className={s.errorBox}>
                      <ErrorIcon fontSize={16} />
                      {error}
                    </div>
                  )}
                  <Button type="submit" appearance="primary" size="large" disabled={loading} className={s.submitBtn}>
                    {loading
                      ? <Spinner size="tiny" />
                      : <>
                          <SendIcon fontSize={18} />
                          {t('cooperate.submit')}
                        </>
                    }
                  </Button>
                </form>
              </Card>
            </div>
          </div>
        </Container>
      </PageSection>

    </div>
  );
}

export default Cooperate;
